import { useEffect, useState } from 'react';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Calendar, Clock, Users } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { ResultOverlay } from '../../components/ResultOverlay';

const statusColors = { pending: 'bg-yellow-500/15 text-yellow-400', confirmed: 'bg-green-500/15 text-green-400',
  cancelled: 'bg-red-500/15 text-red-400', completed: 'bg-blue-500/15 text-blue-400' };
const statusLabels = { pending: 'En attente', confirmed: 'Confirmé', cancelled: 'Annulé', completed: 'Terminé' };
const typeLabels = { event: 'Événement', quote: 'Devis', private: 'Privé', prestation: 'Prestation' };

export default function AdminReservations() {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [overlay, setOverlay] = useState({ show: false, type: 'success', title: '', subtitle: '' });

  const load = () => api.get('/admin/reservations').then(r => setReservations(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const updateStatus = async (rid, status) => {
    try {
      await api.put(`/admin/reservations/${rid}`, { status });
      if (status === 'confirmed') {
        setOverlay({ show: true, type: 'success', title: 'Réservation confirmée !', subtitle: 'Le client a reçu un email' });
      } else if (status === 'cancelled') {
        setOverlay({ show: true, type: 'error', title: 'Réservation annulée', subtitle: 'Le client a été notifié' });
      } else {
        toast.success('Statut mis à jour');
      }
      load();
    } catch { toast.error('Erreur'); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="spin-gold w-8 h-8" /></div>;

  return (
    <div className="fade-in" data-testid="admin-reservations-page">
      <ResultOverlay {...overlay} onClose={() => setOverlay(p => ({ ...p, show: false }))} />
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit' }}>Reservations</h1>
        <Badge className="bg-[#D4AF37]/15 text-[#D4AF37]"><Calendar className="w-3 h-3 mr-1" /> {reservations.length}</Badge>
      </div>

      {reservations.length === 0 ? (
        <div className="text-center py-16 text-[#A1A1AA]">Aucune reservation</div>
      ) : (
        <div className="space-y-3">
          {reservations.map(r => (
            <div key={r.reservation_id} className="bg-[#121212] border border-white/5 rounded-xl p-4" data-testid={`admin-reservation-${r.reservation_id}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-semibold">{r.user_name || 'Client'}</span>
                    <Badge className="text-[10px] bg-white/5">{typeLabels[r.type] || r.type}</Badge>
                    {r.prestation_type && <span className="text-[10px] text-[#D4AF37]">{r.prestation_type}</span>}
                  </div>
                  <div className="text-[10px] text-[#A1A1AA]">{r.user_email}</div>
                </div>
                <Select value={r.status} onValueChange={(v) => updateStatus(r.reservation_id, v)}>
                  <SelectTrigger className="w-32 bg-[#0A0A0A] border-white/10 rounded-lg h-8 text-xs" data-testid={`status-select-${r.reservation_id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10">
                    <SelectItem value="pending">En attente</SelectItem>
                    <SelectItem value="confirmed">Confirme</SelectItem>
                    <SelectItem value="cancelled">Annule</SelectItem>
                    <SelectItem value="completed">Termine</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {r.message && <p className="text-sm text-[#A1A1AA] mb-2">{r.message}</p>}
              <div className="flex gap-4 text-xs text-[#A1A1AA]">
                {r.guests && <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {r.guests} invites</span>}
                {r.date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(r.date).toLocaleDateString('fr-FR')}</span>}
                <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(r.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
